import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {AudienceModel, AudienceRequestModel} from '../../../redux/campaignTypes'
import UserSelector from '../../UserSelector'
import {SearchResultItem} from '../../SearchAndSelectField'
import {Colors} from '../../../constants/colors'
import S2 from '../../typography/S2'

interface Props {
  audience: Extract<AudienceModel, { type: 'individuals' }> | undefined
  onChange: (audience: Extract<AudienceRequestModel, { type: 'individuals' }>) => void
}

const IndividualsAudienceEditor = ({audience, onChange}: Props) => {
  const styles = useStyles()
  const [selectedUsers, setSelectedUsers] = useState<SearchResultItem[]>([])

  useEffect(() => {
    if (!!audience) {
      const mappedUsers = audience.recipients.map(recipient => ({
        name: `${recipient.givenName} ${recipient.familyName} (${recipient.identificationNumber})`,
        value: recipient.id
      }))
      setSelectedUsers(mappedUsers)
    } else {
      setSelectedUsers([])
    }
  }, [audience])

  useEffect(() => {
    const next: AudienceRequestModel = {
      type: 'individuals',
      userIds: selectedUsers.map(u => u.value)
    }
    onChange(next)
  }, [selectedUsers, onChange]);

  const handleUserSelected = (item: SearchResultItem | null) => {
    if (!item) return
    if (selectedUsers.some(x => x.value === item.value)) return
    setSelectedUsers(prev => [...prev, item])
  }

  const handleRemove = (value: string) => {
    setSelectedUsers(prev => prev.filter(u => u.value !== value))
  }

  const renderSelected = () => {
    if (selectedUsers.length === 0) {
      return <S2 className={styles.hint}>No users selected yet. Search to add users.</S2>
    }
    return (
      <div className={styles.chipsContainer}>
        {selectedUsers.map(u => (
          <div className={styles.chip} key={u.value}>
            <span className={styles.chipText}>{u.name}</span>
            <button type='button' className={styles.removeBtn} onClick={() => handleRemove(u.value)}>×</button>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <UserSelector onUserSelected={handleUserSelected} value='' clearOnSelect/>
      {renderSelected()}
    </div>
  )
}

export default IndividualsAudienceEditor

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8,
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 12,
    background: Colors.darkGray3,
  },
  chipText: {
    fontFamily: 'Aestetico-Regular',
    color: Colors.gray0,
  },
  removeBtn: {
    border: 'none',
    background: 'transparent',
    color: Colors.gray0,
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
    width: 20,
    height: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    '&:hover': {
      background: Colors.darkGray4
    }
  },
  hint: {
    color: Colors.gray0,
  }
})
